import { cn, focusRing } from "@simpro/ui";
import { ArrowUpRight, Phone } from "lucide-react";
import { formatPhone } from "@/lib/formatPhone";
import { SectionHeading } from "@/shared/components/SectionHeading";
import { Reveal } from "@/shared/motion/Reveal";
import { page } from "@/shared/styles/layout";
import { pill } from "@/shared/styles/pill";
import type { SalonProfile } from "../schemas/salon.schemas";
import { OpeningHours } from "./OpeningHours";

export interface VisitSectionProps {
  salon: SalonProfile;
  /** A picture of the salon for the arch beside the address, from its own pictures; none, no arch. */
  image?: { url: string; altText: string } | null;
}

/**
 * Where the salon is, how to reach it, when it is open and its policies (design note
 * salon-booking-design-2.md H1, salon-home-landing.md section 5 item 10; TDD-SA FR-SA-004). What the
 * salon offers is the highlights band higher up the page (FR-SA-190). The policies are shown as the
 * salon wrote them.
 *
 * Drawn as the Organic Wellness board's "Come find us" (design note salon-booking-organic-wellness.md
 * section 6 item 10): a picture of the salon in a tall arch, then the address, Directions as the green
 * pill and the number as the sage-outlined one, and the week in a sand card with today as a pale sage
 * pill. A salon with no picture gets the words and the week side by side.
 */
export function VisitSection({ salon, image = null }: VisitSectionProps) {
  const { branch } = salon;
  // Only on a branch's page: a salon with several branches and none chosen shows its picker (FR-SA-245).
  if (!branch) return null;
  const street = [branch.addressLine1, branch.addressLine2].filter(Boolean).join(", ");
  const town = [branch.city, branch.postalCode].filter(Boolean).join(" ");

  const details = (
    <div className="flex flex-col gap-6">
      <SectionHeading id="visit-heading" title="Visit the salon" />
      <p className="text-lg leading-[1.6] text-heading">
        {street}
        <span className="block">{town}</span>
      </p>
      <div className="flex flex-wrap items-center gap-2.5">
        {branch.mapUrl ? (
          <a
            href={branch.mapUrl}
            target="_blank"
            rel="noreferrer"
            className={cn(pill.base, pill.ink, "h-12 px-5 text-[15px]", focusRing)}
          >
            Directions
            <ArrowUpRight className="size-4" aria-hidden />
          </a>
        ) : null}
        <a href={`tel:${branch.phone}`} className={cn(pill.base, pill.outline, "h-12 px-5 text-[15px]", focusRing)}>
          <Phone className="size-4" aria-hidden />
          {formatPhone(branch.phone)}
        </a>
      </div>
    </div>
  );

  const week = (
    <div className="flex flex-col gap-8">
      <OpeningHours hours={salon.hours} holidays={salon.upcomingHolidays} />
      {salon.policies ? (
        <section aria-labelledby="policies-heading" className="flex flex-col gap-2 text-sm leading-relaxed lg:text-[15px]">
          <h3 id="policies-heading" className="font-semibold text-heading">
            Salon policies
          </h3>
          <p className="whitespace-pre-line text-content">{salon.policies}</p>
        </section>
      ) : null}
    </div>
  );

  return (
    <section
      id="visit"
      aria-labelledby="visit-heading"
      className={cn(page, "grid grid-cols-[repeat(auto-fit,minmax(min(100%,18.75rem),1fr))] items-start gap-8 lg:gap-12")}
    >
      {image ? (
        <>
          <Reveal className="arch aspect-[4/5] overflow-hidden bg-muted-bg">
            <img src={image.url} alt={image.altText} loading="lazy" className="size-full object-cover" />
          </Reveal>
          <Reveal index={1} className="flex flex-col gap-8">
            {details}
            {week}
          </Reveal>
        </>
      ) : (
        <>
          {details}
          {week}
        </>
      )}
    </section>
  );
}
